import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, MessageCircle, Send, CheckCircle, AlertCircle } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function ContactUs() {
  const { lang, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    purpose: '',
    message: '',
    consent: false
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.purpose || !formData.consent) {
      alert('Mohon lengkapi seluruh kolom yang wajib diisi dan setujui Kebijakan Privasi.');
      return;
    }

    setStatus('submitting');
    setTimeout(() => {
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        purpose: '',
        message: '',
        consent: false
      });
    }, 1200);
  };

  return (
    <div className="contact-us-page">
      {/* Page Hero */}
      <PageHero eyebrow="Layanan Pelanggan & Konsultasi" title={t('nav_contact')} subtitle="Hubungi tim spesialis Solusi Bangun Beton untuk pertanyaan produk, konsultasi teknikal, atau penawaran proyek." image="/reference-images/ready-mix.webp" />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{t('nav_contact')}</span></li>
          </ul>
        </div>
      </div>

      {/* Main Content Form & Contact Info */}
      <section className="section">
        <div className="container">
          <div className="grid-2">
            {/* Left Column: Office Details */}
            <div>
              <span className="accent-bar"></span>
              <span className="eyebrow">Kantor Operasional Utama</span>
              <h2 className="section-title">Kantor Pusat Solusi Bangun Beton</h2>
              
              <ul className="contact-info-list">
                <li>
                  <MapPin size={22} className="icon-red" />
                  <div>
                    <strong>Alamat Kantor Pusat:</strong>
                    <p>{t('footer_address')}</p>
                  </div>
                </li>
                <li>
                  <Phone size={20} className="icon-red" />
                  <div>
                    <strong>Telepon Kantor:</strong>
                    <p>(021) 2986-1000</p>
                  </div>
                </li>
                <li>
                  <Mail size={20} className="icon-red" />
                  <div>
                    <strong>Email Resmi:</strong>
                    <p>info@solusibangunbeton.com</p>
                  </div>
                </li>
                <li>
                  <MessageCircle size={20} className="icon-red" />
                  <div>
                    <strong>Layanan WhatsApp Fast-Response:</strong>
                    <p>+62 811-1000-8888</p>
                  </div>
                </li>
              </ul>

              <div className="operational-hours-card">
                <strong>Jam Operasional Layanan:</strong>
                <p>Senin – Jumat: 08:00 – 17:00 WIB</p>
                <p>Batching Plant: Operasional 24 Jam (Sesuai Jadwal Pengecoran)</p>
              </div>
            </div>

            {/* Right Column: Interactive Form */}
            <div className="contact-form-card">
              <h3 className="form-card-title">{t('form_title')}</h3>
              <p className="form-card-subtitle">{t('form_subtitle')}</p>

              {status === 'success' ? (
                <div className="form-success-box">
                  <CheckCircle size={40} color="var(--color-success)" />
                  <h4>{t('form_success')}</h4>
                  <button className="btn btn-outline-red btn-sm" onClick={() => setStatus('idle')} style={{ marginTop: '16px' }}>
                    Kirim Pesan Lainnya
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">{t('form_name')} *</label>
                    <input 
                      type="text" 
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Masukkan nama lengkap"
                      required
                    />
                  </div>

                  <div className="grid-2" style={{ gap: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">{t('form_email')} *</label>
                      <input 
                        type="email" 
                        name="email"
                        className="form-control"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="nama@perusahaan.com"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('form_phone')}</label>
                      <input 
                        type="tel" 
                        name="phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0812xxxxxxx"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('form_purpose')} *</label>
                    <select 
                      name="purpose" 
                      className="form-control"
                      value={formData.purpose}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('form_purpose_select')}</option>
                      <option value="Informasi Produk & Harga">{t('form_purpose_product')}</option>
                      <option value="Pemesanan & Pengecekan Status">{t('form_purpose_order')}</option>
                      <option value="Karir & Keanggotaan">{t('form_purpose_career')}</option>
                      <option value="Lainnya">{t('form_purpose_other')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('form_message')}</label>
                    <textarea 
                      name="message" 
                      className="form-control"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tuliskan spesifikasi beton, volume m³, atau lokasi proyek Anda..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-checkbox">
                      <input 
                        type="checkbox" 
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        required
                      />
                      <span>{t('form_consent')}</span>
                    </label>
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-full"
                    disabled={status === 'submitting'}
                  >
                    <Send size={16} />
                    <span>{status === 'submitting' ? t('form_submitting') : t('form_submit')}</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .contact-info-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 28px;
        }

        .contact-info-list li {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          font-size: 15px;
        }

        .contact-info-list strong {
          display: block;
          color: var(--color-ink);
        }

        .contact-info-list p {
          color: var(--color-text-muted);
          font-size: 14px;
        }

        .operational-hours-card {
          background-color: var(--color-surface-subtle);
          padding: 20px;
          border-radius: var(--border-radius-sm);
          border-left: 3px solid var(--color-brand-primary);
          font-size: 13px;
        }

        .contact-form-card {
          background-color: #ffffff;
          border: 1px solid var(--color-border);
          border-radius: var(--border-radius-md);
          padding: 36px;
          box-shadow: var(--shadow-hover);
        }

        .form-card-title {
          font-size: 24px;
          font-weight: 800;
          color: var(--color-ink);
          margin-bottom: 6px;
        }

        .form-card-subtitle {
          font-size: 14px;
          color: var(--color-text-muted);
          margin-bottom: 28px;
        }

        .form-success-box {
          text-align: center;
          padding: 40px 20px;
        }

        .form-success-box h4 {
          font-size: 18px;
          margin-top: 16px;
          color: var(--color-ink);
        }
      `}</style>
    </div>
  );
}
